import { PhoneMissed, UserX, Clock, TrendingDown } from "lucide-react";

const problems = [
  {
    icon: PhoneMissed,
    title: "Missed Calls, Lost Revenue",
    description: "Every missed call is a potential customer gone. Small businesses lose thousands in revenue each month from unanswered calls during busy hours or after closing.",
    stat: "62%",
    statLabel: "of calls to small businesses go unanswered",
  },
  {
    icon: UserX,
    title: "Leads Slip Through the Cracks",
    description: "Without a system to capture every enquiry, potential customers call competitors instead. No follow-up means no conversion.",
    stat: "85%",
    statLabel: "of callers won't call back if unanswered",
  },
  {
    icon: Clock,
    title: "Staff Overwhelmed with Calls",
    description: "Your team spends hours answering repetitive questions about pricing, hours, and availability instead of focusing on the customers in front of them.",
    stat: "3hrs",
    statLabel: "per day spent on routine phone calls",
  },
  {
    icon: TrendingDown,
    title: "No After-Hours Coverage",
    description: "When your office is closed, your phone rings to voicemail. Most customers won't leave a message — they'll just book with someone who answers.",
    stat: "40%",
    statLabel: "of calls come outside business hours",
  },
];

export default function ProblemSection() {
  return (
    <section data-testid="problem-section" className="relative py-10 md:py-[85px] bg-[#0F172A] grain-overlay overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
            The Problem
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight text-balance">
            Your Phone is{" "}
            <span className="text-[#3B82F6]">Costing You Customers</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Small businesses lose leads every day because they can't answer every call. Customers expect instant responses — and they won't wait.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <div
                key={problem.title}
                className={`bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-xl p-6 md:p-8 lg:pr-[37px] hover:bg-white/[0.07] transition-all duration-300 group`}
                data-testid={`problem-card-${index}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center group-hover:bg-[#3B82F6]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#3B82F6]" strokeWidth={1.5} />
                  </div>
                  <div className="text-right">
                    <span className="font-heading font-bold text-2xl text-white">{problem.stat}</span>
                    <p className="text-xs text-slate-500 mt-0.5">{problem.statLabel}</p>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">{problem.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{problem.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
